import { Link } from 'react-router';

import { StateBadge } from '@shared/ui/StateBadge';

import { ColumnButton, Comment } from '../_shared/components';
import type { HeldComment } from '../_shared/types';
import { copy } from './copy';

/**
 * One work item.
 *
 * **A card wrapping the design system's comment.** The card holds the subject
 * line, the optional parent quote, the comment, and the two actions; the comment
 * inside it is the shared anatomy unchanged.
 *
 * **The subject line sits above the comment, not below it.** A moderator needs
 * to know where the words land before reading them, not after forming a view of
 * them. It names the title or the post and links to the surface that owns it.
 *
 * **The queue does not use the held-comment fill, and that is a correctness
 * decision.** The design system's held modifier is the **author's** view, where
 * one held comment stands among published ones and the fill is what tells them
 * apart. In a queue where every item is held the fill distinguishes nothing —
 * and a bare warning fill never appears in a row that also carries an accent
 * action. The held state is carried the way this area carries every state: a
 * badge plus the words around it.
 *
 * **A held reply carries the comment it answers, as context rather than as a
 * second work item.** A reply judged without the line it answers is judged
 * blind. The parent is a sunken quote with no actions and no place in the count.
 */
export function ModerationCard({
  item,
  busy,
  onApprove,
  onReject,
  approveRef,
}: {
  item: HeldComment;
  busy: boolean;
  onApprove: () => void;
  onReject: () => void;
  /**
   * Focus lands here after the card above it is handled: removing the node that
   * held focus would drop the reader to `<body>` and back to the top of the
   * queue.
   */
  approveRef?: (element: HTMLButtonElement | null) => void;
}) {
  const route = subjectRoute(item.subject);

  return (
    <article className="rounded-md border border-border bg-surface">
      <div className="p-4">
        <p className="mb-2 max-w-measure text-xs text-ink-muted">
          {copy.subjectPrefix}{' '}
          {route ? (
            <Link to={route} className="text-accent-text">
              {item.subject.line}
            </Link>
          ) : (
            // A subject type this console has no route for is **named and not
            // linked**. Falling back to the content route would open a title
            // page for a post — the shape renders perfectly and the destination
            // is wrong, which is the failure a fallback must never produce.
            <span className="text-ink">{item.subject.line}</span>
          )}
        </p>

        {item.parent ? (
          <div className="mb-3 rounded-sm border border-border bg-bg-sunken p-3 text-sm text-ink-muted">
            <p className="mb-0 max-w-measure">
              <span className="font-semibold text-ink">
                {item.parent.author.display_name ?? copy.parentLabel}
              </span>
              {' — '}
              {item.parent.body}
            </p>
          </div>
        ) : null}

        <Comment
          author={item.author.display_name ?? ''}
          timestamp={item.created_at}
          body={item.body}
          reply={Boolean(item.parent)}
          // The card is already the `<article>`: the work item is the unit here,
          // and a second one inside it would announce two articles per row.
          nested
          badge={<StateBadge kind="warning">{copy.held}</StateBadge>}
          actions={
            <>
              {/*
                THE PRIMARY, AND THE ONLY RED CONTROL ON THIS VIEW.

                Approving is what the queue exists to do; refusing is the
                exception beside it. Both were drawn as outlines, which left a
                moderation surface with no primary action at all and the
                destructive half louder than the affirmative one — `--danger`
                carries a coloured label and border, the approve carried
                neither.

                The pair also states the design system's own separation by
                FORM rather than by hue (§2.1): the affirmative action is
                filled, the destructive one is outline-only, so 6° of hue
                between `--accent-text` and `--danger` never has to carry the
                distinction.
              */}
              <button
                ref={approveRef}
                type="button"
                onClick={onApprove}
                disabled={busy}
                className="hit-target rounded-sm border border-accent bg-accent px-4 text-sm font-semibold text-accent-ink hover:bg-accent-hover active:bg-accent-press disabled:cursor-not-allowed disabled:opacity-45"
              >
                {copy.actions.approve}
              </button>

              <ColumnButton tone="danger" onClick={onReject} disabled={busy}>
                {copy.actions.reject}
              </ColumnButton>
            </>
          }
        />
      </div>
    </article>
  );
}

/**
 * Where the comment lands.
 *
 * The subject type is the owning service's own vocabulary — `CONTENT_ITEM`,
 * `EDITORIAL_POST`, `COMMENT` — and the two that reach this queue route to two
 * different surfaces. A member with no route resolves to `null` and is **named
 * rather than linked**: sending one subject type to another's surface is a
 * semantic substitution, and it fails silently because the page still renders.
 */
function subjectRoute(subject: HeldComment['subject']): string | null {
  if (subject.type === 'CONTENT_ITEM') return `/content/${subject.id}/edit`;
  if (subject.type === 'EDITORIAL_POST') return `/editorial/${subject.id}`;
  return null;
}
